/**
 * @voltxampere/components — Capacitor definition
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
/**
 * Sprint 35-3 — capacitor variant for explosion animation dispatch.
 *
 * Sprint 23 schema only persisted unstructured params; Sprint 35
 * adds an OPTIONAL `variant` field. Default semantics: `undefined`
 * → electrolytic (Sprint 35-1 damage detector + Sprint 35-3 fx
 * swelling animation both treat plain `capacitor` as electrolytic).
 *
 * Variant differentiation:
 *   - `electrolytic` — 3-stage swelling + popping + smoke (low
 *     destructive threshold 1.0, electrolyte boil-off realistic)
 *   - `ceramic` / `tantalum` / `film` — only "crack" effect, no
 *     swelling (high threshold 1.4)
 */
export const CapacitorVariantSchema = z.enum(["electrolytic", "ceramic", "tantalum", "film"]);
export const CapacitorParamsSchema = z
    .object({
    /** Capacitance in farads; strictly positive. */
    capacitance: z.number().positive().finite(),
    /** Initial voltage across the capacitor at t=0; defaults to 0. */
    initialVoltage: z.number().finite().optional(),
    /** Optional rated voltage. */
    voltageRating: z.number().positive().finite().optional(),
    /**
     * Sprint 35-3 — capacitor variant. Optional + backward-compatible:
     * fixtures from Sprint 23+ without this field hydrate as
     * electrolytic (semantic default).
     */
    variant: CapacitorVariantSchema.optional(),
})
    .strict();
export const capacitorDefinition = {
    type: "capacitor",
    category: "passive",
    displayName: { tr: "Kondansatör", en: "Capacitor" },
    symbol: "C",
    pins: [
        { id: "p1", side: "left", offset: 0, label: "1" },
        { id: "p2", side: "right", offset: 0, label: "2" },
    ],
    parameterSchema: CapacitorParamsSchema,
    nonlinear: false,
};
//# sourceMappingURL=capacitor.js.map